import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { CreateChannelMix, UpdateChannelMix } from '@repo/backend/crt'
import { CHANNEL_MIX_CREATE_ERROR_TYPES, PRODUCT_CATEGORIES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const channelMixSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export const channelMixDetailsSearchSchema = z.object({
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  province: z.array(z.string()).optional(),
  distributionChannel: z.array(z.string()).optional(),
  marked: z.boolean().optional(),
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export type ChannelMixSearch = z.infer<typeof channelMixSearchSchema>
export type ChannelMixDetailsSearch = z.infer<typeof channelMixDetailsSearchSchema>

export type ChannelMixes = Awaited<ReturnType<typeof getChannelMixes>>
export type ChannelMix = ChannelMixes['results'][number]
export type ChannelMixWithDetails = Awaited<ReturnType<typeof getChannelMixById>>

type UseCreateChannelMixArgs = {
  queries: ChannelMixSearch
  handleOnSuccess: (channelMixId: number) => void
}

type UseUpdateChannelMixArgs = QueryHandlers & {
  channelMixId: number
}

type UseDeleteChannelMixesArgs = QueryHandlers & {
  channelMixIds: number[]
  queries: ChannelMixSearch
}

async function getCurrentChannelMix(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['channel-mix'].current.$get(
    { query: stringifyValues({ crtPeriod }) },
    { init: { signal } },
  )
  return await res.json()
}

// The channel mix imported for a CRT period (the current one when omitted), or `null` when that period
// has none: a period never falls back to another period's mix.
export const currentChannelMixQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentChannelMix', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getCurrentChannelMix(crtPeriod, signal),
})

export function useCurrentChannelMix(crtPeriod?: string, options: { enabled?: boolean } = {}) {
  return useQuery({ ...currentChannelMixQueryOptions(crtPeriod), enabled: options.enabled })
}

async function getChannelMixes(search: ChannelMixSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['channel-mix'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function getChannelMixById(
  id: string,
  queries: ChannelMixDetailsSearch = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['channel-mix'][':id'].$get(
    {
      param: { id },
      query: stringifyValues(queries),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function createChannelMix(channelMixDetails: CreateChannelMix) {
  const api = getApi()

  const res = await api.crt['channel-mix'].$post({
    json: channelMixDetails,
  })

  return await res.json()
}

async function updateChannelMix(channelMixId: number, details: UpdateChannelMix) {
  const api = getApi()

  await api.crt['channel-mix'][':id'].$patch({
    param: { id: String(channelMixId) },
    json: details,
  })
}

async function deleteChannelMixes(channelMixIds: number[]) {
  const api = getApi()

  await api.crt['channel-mix'].bulk.$delete({
    json: { ids: channelMixIds },
  })
}

export function useChannelMixes(queries: ChannelMixSearch) {
  return useQuery({
    queryKey: ['channelMixes', queries],
    queryFn: ({ signal }) => getChannelMixes(queries, signal),
  })
}

export const channelMixByIdQueryOptions = (id: string, queries: ChannelMixDetailsSearch = {}) => ({
  queryKey: ['channelMixById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getChannelMixById(id, queries, signal),
})

export function useChannelMixById(id: string, queries: ChannelMixDetailsSearch = {}) {
  return useQuery(channelMixByIdQueryOptions(id, queries))
}

async function getChannelMixDetailsCsv(
  id: string,
  queries: ChannelMixDetailsSearch,
  signal?: AbortSignal,
) {
  const stringifiedQueries = stringifyValues(queries)

  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )

  const queryParams = new URLSearchParams(filteredQueries).toString()

  const csvExport = await fetchCsvExport(
    `/crt/channel-mix/${id}/details/export?${queryParams}`,
    signal,
  )
  return csvExport
}

export const channelMixDetailsExportQueryOptions = (
  id: string,
  queries: ChannelMixDetailsSearch,
) => ({
  queryKey: ['channelMixDetailsExport', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getChannelMixDetailsCsv(id, queries, signal),
})

async function getChannelMixDetailsForRawExport(id: string, signal?: AbortSignal) {
  const csvExport = await fetchCsvExport(`/crt/channel-mix/${id}/details/raw-export`, signal)
  return csvExport
}

export const channelMixDetailsRawExportQueryOptions = (id: string) => ({
  queryKey: ['channelMixDetailsRawExport', id],
  queryFn: ({ signal }: { signal: AbortSignal }) => getChannelMixDetailsForRawExport(id, signal),
})

export function useCreateChannelMix({ queries, handleOnSuccess }: UseCreateChannelMixArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['channelMixes', queries],
    mutationFn: createChannelMix,
    onSuccess: (channelMix) => {
      handleOnSuccess(channelMix.id)

      queryClient.invalidateQueries({
        queryKey: ['channelMixes', queries],
      })
      queryClient.invalidateQueries({ queryKey: ['currentChannelMix'] })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('channelMix.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; crtPeriod?: string }
        | undefined

      let description: string
      switch (customParams?.type) {
        case CHANNEL_MIX_CREATE_ERROR_TYPES.crtPeriodConflict:
          description = `${t('errors.channelMix.createFailed', { ns: 'crt' })}: ${t(
            'errors.channelMix.crtPeriodConflict',
            { ns: 'crt', crtPeriod: customParams.crtPeriod },
          )}`
          break
        default:
          description = getFirstZodErrorMessage(issues, t, 'errors.channelMix.createFailed', 'crt')
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateChannelMix({ channelMixId, handleOnSuccess }: UseUpdateChannelMixArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateChannelMix', channelMixId],
    mutationFn: (details: UpdateChannelMix) => updateChannelMix(channelMixId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['channelMixById', String(channelMixId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('channelMix.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.channelMix.updateFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteChannelMixes({
  channelMixIds,
  queries,
  handleOnSuccess,
}: UseDeleteChannelMixesArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteChannelMixes'],
    mutationFn: () => deleteChannelMixes(channelMixIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['channelMixes', queries],
      })
      queryClient.invalidateQueries({ queryKey: ['currentChannelMix'] })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('channelMix.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.channelMix.deleteFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
